import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useLocation, useParams } from 'react-router-dom';
import { string, object } from 'yup';
import { useFormik } from 'formik';

// Store
import { Dispatch, RootState } from '../../store/store';

// Constants
import { TEXT_LENGTH_LIMITS } from '../../constants/common';

// Components
import InteractiveButton from '../../components/InteractiveButton/InteractiveButton';
import InputControl from '../../components/InputControl/InputControl';
import SpinnerLoader from '../../components/SpinnerLoader/SpinnerLoader';
import useSafeRequest from '../../hooks/useSafeRequest';

type EditScreenProps = ReturnType<typeof mapState> &
  ReturnType<typeof mapDispatch>;

type FormValues = {
  name: string | null;
  contractId: string | null;
};

const EditScreen: React.FC<EditScreenProps> = ({
  updateScreen,
  contract,
  getContractById,
  getOneById,
  screen,
  resetState,
  getLocationById,
}) => {
  const location = useLocation();
  const { contractId, screenId, clientId } = useParams();

  const data: { contractId?: string } | null = location.state;
  const [isLoading, setIsLoading] = useState(true);

  const formik = useFormik<FormValues>({
    initialValues: {
      name: screen?.name || '',
      contractId: (data?.contractId || contractId) ?? '',
    },
    validationSchema: object({
      name: string()
        .required('Required')
        .min(TEXT_LENGTH_LIMITS.MIN, `Min first name length is ${TEXT_LENGTH_LIMITS.MIN} characters`)
        .max(TEXT_LENGTH_LIMITS.MAX, `Max first name length is ${TEXT_LENGTH_LIMITS.MAX} characters`),
      contractId: string().required('Required')
    }),
    onSubmit: async (values) => {
      await updateScreen({
        screenId: screenId!,
        payload: {
          name: values.name,
        },
      });
    },
  });

  const isDisabledBtn = Boolean((formik.touched?.name && formik.errors?.name) || (formik.touched?.contractId && formik.errors?.contractId));

  const fetchData = async (params: { contractId: string, screenId: string, signal?: AbortSignal }) => {
    try {
      const { contractId, ...restParams } = params;
      await getContractById(contractId ?? screen.contractId);
      const data = await getOneById(restParams);
      await getLocationById(clientId);
      formik.setValues({
        name: data.name,
        contractId: data.contractId,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const resetData = () => {
    resetState();
    formik.resetForm();
  }

  useEffect(() => {
    resetData();
    fetchData({ contractId, screenId });

    return () => {
      resetData();
    }
  }, [screenId]);

  useSafeRequest({
    request: fetchData,
    params: { contractId, screenId },
    options: {
      deps: [screenId],
      reset: resetData,
    }
  });

  return (
    <>
      {isLoading && (
        <div className="h-full w-full flex items-center justify-center">
          <SpinnerLoader />
        </div>
      )}
      {!isLoading && (
        <div className="p-edit-screen pb-4 md:pb-25">
          <div className="rounded-sm border border-stroke bg-white shadow-default dark:border-strokedark dark:bg-boxdark">
            <div className="border-b border-stroke px-7 py-4 dark:border-strokedark">
              <h3 className="font-medium text-black dark:text-white">
                Edit Screen
              </h3>
            </div>
            <form className="flex w-full flex-col gap-5.5 p-7" onSubmit={formik.handleSubmit} >
              <InputControl
                label="Name"
                type="text"
                name="name"
                placeholder="Enter your screen's name"
                value={formik.values.name}
                customOnChange={formik.handleChange}
                onBlur={formik.handleBlur}
                showError={formik.touched.name && formik.errors.name}
                errorText={formik.errors.name}
              />
              <InputControl
                label="Contract"
                type="text"
                name="contractId"
                value={contract?.name || ''}
                disabled={true}
                className="bg-gray-100 text-gray-500"
              />
              <div className="flex justify-end gap-4.5">
                <InteractiveButton
                  text="Save"
                  submit
                  disabled={isDisabledBtn}
                  isLoading={formik.isSubmitting}
                />
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
};

const mapState = (state: RootState) => ({
  contract: state.contracts.contract,
  screen: state.screens.screen,
});

const mapDispatch = (dispatch: Dispatch) => ({
  getContractById: dispatch.contracts.getContract,
  getOneById: dispatch.screens.getOneById,
  updateScreen: dispatch.screens.update,
  resetState: dispatch.screens.resetState,
  getLocationById: dispatch.locations.getLocation,
});

export default connect(mapState, mapDispatch)(EditScreen);
